import React, { useCallback, useContext, useMemo, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { useQuery } from "@apollo/client";
import {
  CNC_GET_DATABASE_INSTANCES,
  NEW_ACCOUNT,
  type CncGetDatabaseInstancesData,
  type NewAccountData,
  type NewAccountVariables,
} from "../CustomerProvisioningApi";
import { showToast } from "../../Forms/Dialogs/Toast/Toast";
import {
  TextButton,
  TextInput,
  SelectInput,
  useMutationWrapper,
  ModalContext,
  ModalButtonsContainer,
  showErrorDialog,
  REQUIRED_ERROR_MESSAGE,
} from "@SeemplicityDev/shared";

import { ACCOUNT_TYPES, type AccountType } from "../../utils";

interface NewAccountProps {
  organizationSlug: string;
  organizationName: string;
}

interface NewAccountFormValues {
  accountName: string;
  databaseIdentifier: string;
  databaseSchema: string;
  accountType: AccountType;
}

interface SelectOption<TValue> {
  label: TValue;
  value: TValue;
}

const NewAccount: React.FC<NewAccountProps> = ({
  organizationSlug,
  organizationName,
}) => {
  const [createNewAccount] = useMutationWrapper<
    NewAccountData,
    NewAccountVariables
  >(NEW_ACCOUNT, {
    refetchQueries: ["cnc_organizations"],
  });
  const { closeModal } = useContext(ModalContext);
  const [isSubmittingForm, setIsSubmittingForm] = useState(false);
  const {
    handleSubmit,
    control,
    formState: { errors, isValid, isDirty },
    setValue,
  } = useForm<NewAccountFormValues>({ mode: "all" });

  const { data: databaseInstancesData, loading: isLoadingDatabaseInstances } =
    useQuery<CncGetDatabaseInstancesData>(CNC_GET_DATABASE_INSTANCES, {
      fetchPolicy: "cache-and-network",
    });

  const databaseInstanceOptions = useMemo<SelectOption<string>[]>(
    () =>
      (databaseInstancesData?.cnc_database_instances ?? []).map(
        (identifier) => ({
          label: identifier,
          value: identifier,
        })
      ),
    [databaseInstancesData]
  );

  const onSubmit = useCallback(
    (data: NewAccountFormValues) => {
      setIsSubmittingForm(true);
      createNewAccount({
        variables: {
          organization_slug: organizationSlug,
          new_account_input: {
            name: data.accountName,
            database_identifier: data.databaseIdentifier,
            database_schema: data.databaseSchema,
            account_type: data.accountType,
          },
        },
      })
        .then((res) => {
          setIsSubmittingForm(false);
          if (!!res.data?.cnc_new_account?.ok) {
            closeModal();
            showToast({
              message: res.data?.cnc_new_account?.msg,
            });
          } else {
            showErrorDialog({ message: res.data?.cnc_new_account?.msg });
          }
        })
        .catch((err: Error) => {
          setIsSubmittingForm(false);
          showErrorDialog({ message: err.message });
        });
    },
    [createNewAccount, organizationSlug, closeModal]
  );

  const validateSchema = useCallback((schema: string) => {
    if (!schema) {
      return "Missing schema";
    }
    if (schema.length < 2) {
      return "Schema must be at least 2 characters long";
    }
    if (/\s+/.test(schema)) {
      return "Schema cannot contain spaces";
    }
    if (schema.toLowerCase() !== schema) {
      return "Schema must be lowercase";
    }
    if (schema.startsWith("pg_") || schema.startsWith("_")) {
      return "Schema shouldn't start with 'pg_' or '_'";
    }
    if (!/^[a-z]+/.test(schema)) {
      return "Schema name must start with a lowercase letter";
    }
    return true;
  }, []);

  const setDatabaseSchemaAccordingToName = useCallback(
    (name: string) => {
      if (!name) {
        return;
      }
      const sanitizedAccountName = name.toLowerCase().replace(/\s+/g, "_");
      setValue("databaseSchema", `${organizationSlug}_${sanitizedAccountName}`);
    },
    [organizationSlug, setValue]
  );

  const accountTypeOptions: SelectOption<AccountType>[] = Object.values(
    ACCOUNT_TYPES
  ).map((type) => ({
    label: type,
    value: type,
  }));

  return (
    <div>
      <div className="text-medium">
        <strong>Organization:</strong> {organizationName} ({organizationSlug})
      </div>
      <form
        className="create-org-account-modal"
        onSubmit={handleSubmit(onSubmit)}
      >
        <Controller
          name={"accountName"}
          render={({ field: { value, onChange, onBlur } }) => {
            return (
              <TextInput
                type={"text"}
                value={value}
                data-testid={"accountName"}
                labelTop
                onBlur={onBlur}
                name={"accountName"}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  onChange(e);
                  setDatabaseSchemaAccordingToName(e.target.value);
                }}
                label={"Account Name"}
                placeholder={"Prod / Dev / etc..."}
                error={errors.accountName ? errors.accountName : null}
                required
              />
            );
          }}
          control={control}
          rules={{
            required: REQUIRED_ERROR_MESSAGE,
          }}
        />
        <Controller
          name={"databaseIdentifier"}
          render={({ field: { value, onChange, onBlur } }) => {
            return (
              <SelectInput
                options={databaseInstanceOptions}
                value={
                  databaseInstanceOptions.find(
                    (option) => option.value === value
                  ) || null
                }
                onChange={(selectedValue: SelectOption<string> | null) => {
                  return onChange(selectedValue?.value);
                }}
                onBlur={onBlur}
                name={"databaseIdentifier"}
                data-testid={"databaseIdentifier"}
                labelTop
                label={"Database Instance Identifier"}
                placeholder={
                  isLoadingDatabaseInstances
                    ? "Loading..."
                    : "Select Database Instance"
                }
                isLoading={isLoadingDatabaseInstances}
                disabled={isLoadingDatabaseInstances}
                error={errors.databaseIdentifier ?? undefined}
                required
              />
            );
          }}
          control={control}
          rules={{
            required: REQUIRED_ERROR_MESSAGE,
          }}
        />
        <Controller
          name={"databaseSchema"}
          render={({ field: { value, onChange, onBlur } }) => {
            return (
              <TextInput
                type={"text"}
                value={value}
                data-testid={"databaseSchema"}
                labelTop
                onBlur={onBlur}
                name={"databaseSchema"}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  onChange(e);
                }}
                label={"Database Schema Name"}
                placeholder={"The database schema"}
                error={errors.databaseSchema ? errors.databaseSchema : null}
                required
              />
            );
          }}
          control={control}
          rules={{
            required: REQUIRED_ERROR_MESSAGE,
            validate: validateSchema,
          }}
        />
        <Controller
          name={"accountType"}
          render={({ field: { value, onChange, onBlur } }) => {
            return (
              <SelectInput
                options={accountTypeOptions}
                value={
                  accountTypeOptions.find((type) => type.value === value) ||
                  null
                }
                onChange={(selectedValue: SelectOption<AccountType> | null) => {
                  return onChange(selectedValue?.value);
                }}
                onBlur={onBlur}
                name={"accountType"}
                labelTop
                label={"Account Type"}
                placeholder={"Select Account Type"}
                error={errors.accountType ?? undefined}
                required
              />
            );
          }}
          control={control}
          rules={{
            required: REQUIRED_ERROR_MESSAGE,
          }}
        />
        <ModalButtonsContainer>
          <TextButton
            type="button"
            data-testid={"create-account-cancel"}
            label={"Cancel"}
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation();
              closeModal();
            }}
          />
          <TextButton
            type="submit"
            label={"Create Account"}
            data-testid={"create-account"}
            buttonStyle={"primary"}
            isSubmitting={isSubmittingForm}
            rightIcon={isSubmittingForm ? "seem-icon-spinner spinner" : ""}
            disabled={!isValid || !isDirty || isSubmittingForm}
          />
        </ModalButtonsContainer>
      </form>
    </div>
  );
};
export default NewAccount;
